/**
 * Podpis osoby piszącej ze skrzynki zakupy@.
 *
 * Propozycja modelu (skrzynka) i szablon startowy (nowa kampania) kończą się
 * bezosobową stopką działu („Pozdrawiam" / „Dział zakupów GK ELEKTIM"). Imię,
 * nazwisko i telefon wchodzą MIĘDZY te dwie linie już w edytorze, gdy człowiek
 * otwiera wątek albo zakłada kampanię — widzi podpis i może go poprawić.
 * Do 07.09.2026 w skrzynce doklejała go dopiero wysyłka, a ekran tylko
 * zapowiadał, że doklei; ludzie zgłaszali, że „nic się nie pojawia". Backend
 * dopisuje podpis do odpowiedzi nadal, ale wyłącznie jako bezpiecznik, gdy
 * w treści nie ma nazwiska. Kampania idzie dokładnie z tym, co w szablonie.
 *
 * Skrzynka oddaje HTML z edytora, szablon kampanii bywa zwykłym tekstem — obie
 * postacie mają swoją ścieżkę: w HTML-u wierszem jest `<br>`, w tekście znak
 * nowej linii.
 *
 * Reguły są lustrem `apps.leads.inbox.with_personal_signature` — jedna zmiana
 * tu wymaga tej samej tam.
 */

export interface SignatureUser {
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
}

/**
 * Telefon jak na wizytówce: „+48 601 234 567". Dziewięć cyfr bez kierunkowego
 * to numer polski. Numer zagraniczny (długość kierunkowego jest niejednoznaczna)
 * i zapis, którego nie rozumiemy, wracają tak, jak je wpisano.
 */
export function formatSignaturePhone(phone: string | null | undefined): string {
  const raw = (phone ?? "").trim();
  if (!raw) return "";
  let compact = raw.replace(/[\s\-().]/g, "");
  if (compact.startsWith("0048")) compact = `+48${compact.slice(4)}`;

  let digits: string;
  if (compact.startsWith("+48")) digits = compact.slice(3);
  else if (/^48\d{9}$/.test(compact)) digits = compact.slice(2);
  else if (/^\d{9}$/.test(compact)) digits = compact;
  else return raw;
  if (!/^\d{9}$/.test(digits)) return raw;
  return `+48 ${digits.replace(/(\d{3})(?=\d)/g, "$1 ")}`;
}

/** Wiersze podpisu: imię i nazwisko, pod spodem telefon (jeśli jest). */
export function signatureLines(user: SignatureUser | null | undefined): string[] {
  const name = [user?.first_name, user?.last_name]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  if (!name) return [];
  const phone = formatSignaturePhone(user?.phone_number);
  return phone ? [name, phone] : [name];
}

/**
 * Pozdrowienie, po którym wchodzi podpis. „Pozdrawiamy" łapiemy razem z formą
 * pojedynczą, bo pisze je niejeden model i niejeden szablon — a pod osobistym
 * podpisem liczba mnoga brzmi jak podpis komisji.
 */
const REGARDS_RE = /\bpozdrawiam(y)?\b/i;
const REGARDS_ALL_RE = /\bpozdrawiam(y)?\b/gi;
const LINE_BREAK_RE = /<br\s*\/?>|<\/(p|div|li)>/i;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

/** Ta sama reguła co po stronie backendu: zaczyna się od znacznika = HTML. */
function looksLikeHtml(text: string): boolean {
  return text.startsWith("<") && text.includes(">");
}

/**
 * Wstawia podpis pod „Pozdrawiam", nad linię działu; bez pozdrowienia — na
 * koniec. „Pozdrawiamy" prostuje na liczbę pojedynczą, bo pod imieniem jednej
 * osoby czyta się jak podpis komisji. Idempotentne po nazwisku: kto już jest
 * podpisany (własną ręką albo z zapisanej wersji roboczej), drugiego podpisu
 * nie dostaje.
 *
 * HTML z edytora i zwykły tekst szablonu obsługujemy osobno — patrz nagłówek
 * pliku.
 */
export function withPersonalSignature(text: string, lines: string[]): string {
  let value = (text ?? "").trim();
  if (!lines.length || !value) return value;
  value = value.replace(REGARDS_ALL_RE, "Pozdrawiam");
  if (stripTags(value).toLocaleLowerCase().includes(lines[0].toLocaleLowerCase())) {
    return value;
  }
  const regards = REGARDS_RE.exec(value);

  if (!looksLikeHtml(value)) {
    const block = lines.join("\n");
    if (!regards) return `${value}\n${block}`;
    const lineEnd = value.indexOf("\n", regards.index + regards[0].length);
    if (lineEnd === -1) return `${value}\n${block}`;
    return `${value.slice(0, lineEnd)}\n${block}${value.slice(lineEnd)}`;
  }

  const block = lines.map(escapeHtml).join("<br>");
  if (regards) {
    const after = value.slice(regards.index + regards[0].length);
    const lineEnd = LINE_BREAK_RE.exec(after);
    if (lineEnd) {
      const at = regards.index + regards[0].length + lineEnd.index;
      return `${value.slice(0, at)}<br>${block}${value.slice(at)}`;
    }
  }
  const closing = /<\/(p|div|li)>\s*$/i.exec(value);
  if (closing) {
    return `${value.slice(0, closing.index)}<br>${block}${value.slice(closing.index)}`;
  }
  return `${value}<p>${block}</p>`;
}
